// ww-0007

class CartExpressShipping extends HTMLElement {
  connectedCallback() {
    // Excluded cart (e.g. embroidered product): drop an express item that is already in the cart.
    if (this.getAttribute('data-remove-express') === 'true') {
      this._removeExcludedExpress();
      return;
    }

    this.checkbox = this.querySelector('.sh-cart-express-shipping__checkbox');
    this.toggle = this.querySelector('.sh-cart-express-shipping__toggle');

    if (!this.checkbox) return;

    this._onToggleChange = this._onToggleChange.bind(this);
    this._syncToggleState = this._syncToggleState.bind(this);

    this.checkbox.addEventListener('change', this._onToggleChange);
    document.documentElement.addEventListener('cart:updated', this._syncToggleState);
    document.documentElement.addEventListener('cart:refresh', this._syncToggleState);
  }

  disconnectedCallback() {
    if (!this.checkbox) return;

    this.checkbox.removeEventListener('change', this._onToggleChange);
    document.documentElement.removeEventListener('cart:updated', this._syncToggleState);
    document.documentElement.removeEventListener('cart:refresh', this._syncToggleState);
  }

  get variantId() {
    return this.getAttribute('data-variant-id');
  }

  _getExpressItems(cart) {
    if (!cart?.items) return [];

    return cart.items.filter((item) => String(item.variant_id) === String(this.variantId));
  }

  _getCurrencyCode() {
    return (
      window.Shopify?.currency?.active ||
      window.themeVariables?.settings?.cartCurrency ||
      'EUR'
    );
  }

  _pushToggleEvent(state) {
    const eventName = state === 'on' ? 'express_shipping_toggle_on' : 'express_shipping_toggle_off';
    const priceCents = parseInt(this.getAttribute('data-variant-price'), 10) || 0;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      goal_name: eventName,
      express_shipping_state: state,
      action_source: 'cart_drawer_express_toggle',
      cart_type: 'cart_drawer',
      variant_id: this.variantId,
      product_id: this.getAttribute('data-product-id') || '',
      product_handle: this.getAttribute('data-product-handle') || '',
      product_title: this.getAttribute('data-product-title') || '',
      value: priceCents / 100,
      currency: this._getCurrencyCode()
    });
  }

  _dispatchCartEvents(cartContent, replacementDelay) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cartContent }
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart: cartContent,
          replacementDelay
        }
      })
    );
  }

  async _fetchCart() {
    const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
    return response.json();
  }

  async _syncToggleState(event) {
    if (!this.checkbox) return;

    let cart = event?.detail?.cart;

    if (!cart?.items) {
      cart = await this._fetchCart();
    }

    this.checkbox.checked = this._getExpressItems(cart).length > 0;
  }

  async _onToggleChange(event) {
    const enabled = event.target.checked;

    if (this.toggle) {
      this.toggle.setAttribute('aria-busy', 'true');
    }

    this.checkbox.disabled = true;

    try {
      if (enabled) {
        await this._addExpressProduct();
      } else {
        await this._removeExpressProduct();
      }

      this._pushToggleEvent(enabled ? 'on' : 'off');
    } catch (error) {
      console.error('Express shipping toggle failed:', error);
      await this._syncToggleState({});
      throw error;
    } finally {
      this.checkbox.disabled = false;

      if (this.toggle) {
        this.toggle.removeAttribute('aria-busy');
      }
    }
  }

  async _addExpressProduct() {
    const cart = await this._fetchCart();

    if (this._getExpressItems(cart).length > 0) {
      this.checkbox.checked = true;
      return;
    }

    const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [
          {
            id: this.variantId,
            quantity: 1,
            properties: {
              _express_shipping: 'true'
            }
          }
        ],
        sections: ['mini-cart']
      })
    });

    const responseJson = await response.json();

    if (!response.ok) {
      throw new Error(responseJson.description || 'Could not add express shipping product');
    }

    const cartContent = await this._fetchCart();
    cartContent.sections = responseJson.sections;
    this._dispatchCartEvents(cartContent, 300);
  }

  async _removeExcludedExpress() {
    if (CartExpressShipping._removingExcluded) return;
    CartExpressShipping._removingExcluded = true;

    try {
      const cart = await this._fetchCart();
      const updates = {};
      this._getExpressItems(cart).forEach((item) => {
        updates[item.key] = 0;
      });
      if (Object.keys(updates).length === 0) return;

      const response = await fetch(`${window.themeVariables.routes.cartUrl}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, sections: ['mini-cart'] })
      });
      const cartContent = await response.json();
      if (!response.ok) throw new Error(cartContent.description || 'Could not remove express shipping product');

      this._pushToggleEvent('off_auto');
      this._dispatchCartEvents(cartContent, 0);
    } catch (error) {
      console.error('Express shipping auto-removal failed:', error);
    } finally {
      CartExpressShipping._removingExcluded = false;
    }
  }

  async _removeExpressProduct() {
    const cart = await this._fetchCart();
    const expressItems = this._getExpressItems(cart);

    if (expressItems.length === 0) {
      this.checkbox.checked = false;
      return;
    }

    const updates = {};

    expressItems.forEach((item) => {
      updates[item.key] = 0;
    });

    const response = await fetch(`${window.themeVariables.routes.cartUrl}/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        updates,
        sections: ['mini-cart']
      })
    });

    const cartContent = await response.json();

    if (!response.ok) {
      throw new Error(cartContent.description || 'Could not remove express shipping product');
    }

    this._dispatchCartEvents(cartContent, 600);
  }
}

if (!window.customElements.get('cart-express-shipping')) {
  window.customElements.define('cart-express-shipping', CartExpressShipping);
}
